import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import {
  WorkflowService,
  WorkflowContextService,
  Utilities
} from 'src/app/services';
import { Workflow } from 'src/app/models';
import {
  DataSetField,
  ReportDataSetFieldDataType,
  WorkflowFilter
} from 'src/app/models/custom-report';
import { ItemSearchOptionFieldOption } from 'src/app/components/filter-list/models/filterClasses';
import {
  FilterBuilderFilter,
  FilterBuilderFilterFlat,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import {
  DataSet,
  WorkflowApplicationReportDataSet
} from 'src/app/models/report-data-set';
import { DateDataEntity, FreeFormTextDataEntity } from 'src/app/models/data-entities';
import { of } from 'rxjs';

interface Column {
  id: string;
  name: string;
  resultHeader: string;
  dataType: number;
  mappings: any[];
  outputFormula: any;
  conditionalOutput: boolean;
  isStatic?: boolean;
}

interface WorkflowRow {
  rowKey: string;
  workflow: string;
  workflowId?: string | null;
  [key: string]: any;
  workflowFilterConfig?: WorkflowFilter | null;
  filterParams?: FilterBuilderParam[];
  filters?: any[];
  filterBuilderId?: string;
}

@Component({
  selector: 'wm-dataset-add-workflows',
  standalone: false,
  templateUrl: './dataset-add-workflows.component.html',
  styleUrls: ['./dataset-add-workflows.component.scss']
})
export class DatasetAddWorkflowsComponent implements OnChanges {
  rows: WorkflowRow[] = [];
  private workflowFilterParamsCache: {
    [workflowId: string]: FilterBuilderParam[];
  } = {};
  private workflowRowKeys: string[] = [];

  @Output() datasetChange = new EventEmitter<any>();
  @Output() openColumnSettingsRequested = new EventEmitter<{
    workflowId: string | null;
    field?: any | null;
  }>();
  @Input() dataSetConfig: WorkflowApplicationReportDataSet;
  @Input() availableWorkflows: Workflow[] = [];

  columns: Column[] = [];
  filterParams: any[] = [];
  showFilterBuilder = false;
  filterWorkflowId: string | null = null;
  selectedWorkflowId: string | null = null;
  externalFilterInputs: any[] = [];
  cellDropdownEntities: any[] = [];
  workflowDisplayNameResolver = (
    workflowId?: string | null,
    fallback = 'Select Workflow'
  ) => this.getWorkflowDisplayName(workflowId, fallback);
  public filtersReady = false;
  workflowFilterConfig: WorkflowFilter;
  workflowFilterParams: FilterBuilderParam[];
  workflowFilters: FilterBuilderFilter[];
  dataSet: WorkflowApplicationReportDataSet;
  ALL_WORKFLOWS_ID = Utilities.EMPTY_GUID;
  ds: DataSet;

  constructor(
    private _workflowSvc: WorkflowService,
    private _context: WorkflowContextService
  ) {}

  /* ---------------------------------- */
  /* INIT                               */
  /* ---------------------------------- */
  @Output() workflowSelected = new EventEmitter<string>();


  get isNoData(){
    return !this.dataSetConfig || this.dataSetConfig.workflowIds.length === 0;

  }
  get hasFewData() {
    const validWorkflowIds = this.dataSetConfig?.workflowIds?.filter(w => w !== '') || [];
  
    return validWorkflowIds.length > 0 && validWorkflowIds.length <= 2;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.dataSetConfig) {
      return;
    }

    this.ensureDataSetConfigDefaults();

    if (changes['dataSetConfig'] || changes['availableWorkflows']) {
      this.refreshViewFromDataSet();
    }
  }

  private ensureDataSetConfigDefaults() {
    if (!this.dataSetConfig) {
      return;
    }

    if (!Array.isArray(this.dataSetConfig.fields)) {
      this.dataSetConfig.fields = [];
    }

    if (!Array.isArray(this.dataSetConfig.workflowIds)) {
      this.dataSetConfig.workflowIds = [];
    }

    if (!Array.isArray(this.dataSetConfig.workflowFilters)) {
      this.dataSetConfig.workflowFilters = [];
    }

    this.ensureWorkflowFiltersAligned();
  }

  private createRowKey(): string {
    return `workflow_row_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  }

  private syncWorkflowRowKeys() {
    const workflowIds = this.dataSetConfig?.workflowIds || [];

    while (this.workflowRowKeys.length < workflowIds.length) {
      this.workflowRowKeys.push(this.createRowKey());
    }

    if (this.workflowRowKeys.length > workflowIds.length) {
      this.workflowRowKeys = this.workflowRowKeys.slice(0, workflowIds.length);
    }
  }

  private createWorkflowFilterConfig(workflowId?: string | null): WorkflowFilter {
    const workflowFilter = new WorkflowFilter();
    workflowFilter.workflowId = workflowId || '';
    workflowFilter.filtersList = [];

    return workflowFilter;
  }

  private cloneWorkflowFilterConfig(
    workflowFilter: WorkflowFilter | null | undefined,
    workflowId?: string | null
  ): WorkflowFilter {
    const clonedWorkflowFilter = new WorkflowFilter();
    clonedWorkflowFilter.workflowId = workflowId || workflowFilter?.workflowId || '';
    clonedWorkflowFilter.filtersList = (workflowFilter?.filtersList || []).map(
      filter => new ItemSearchOptionFieldOption({ ...filter })
    );

    return clonedWorkflowFilter;
  }

  private ensureWorkflowFiltersAligned() {
    const workflowIds = this.dataSetConfig?.workflowIds || [];
    const existingWorkflowFilters = this.dataSetConfig?.workflowFilters || [];
    const workflowFilterQueue: { [workflowId: string]: WorkflowFilter[] } = {};

    existingWorkflowFilters.forEach(filter => {
      const workflowId = filter?.workflowId || '';

      if (!workflowFilterQueue[workflowId]) {
        workflowFilterQueue[workflowId] = [];
      }

      if (!Array.isArray(filter.filtersList)) {
        filter.filtersList = [];
      }

      workflowFilterQueue[workflowId].push(filter);
    });

    this.dataSetConfig.workflowFilters = workflowIds.map(workflowId => {
      const nextFilter = (workflowFilterQueue[workflowId || ''] || []).shift();

      if (nextFilter) {
        nextFilter.workflowId = workflowId || '';
        return nextFilter;
      }

      return this.createWorkflowFilterConfig(workflowId);
    });
  }

  private getWorkflowFilterConfigAtIndex(index: number): WorkflowFilter {
    const workflowId = this.dataSetConfig?.workflowIds?.[index] || '';

    if (!Array.isArray(this.dataSetConfig.workflowFilters)) {
      this.dataSetConfig.workflowFilters = [];
    }

    while (this.dataSetConfig.workflowFilters.length <= index) {
      const nextWorkflowId =
        this.dataSetConfig.workflowIds[this.dataSetConfig.workflowFilters.length] || '';
      this.dataSetConfig.workflowFilters.push(
        this.createWorkflowFilterConfig(nextWorkflowId)
      );
    }

    const workflowFilter =
      this.dataSetConfig.workflowFilters[index] ||
      this.createWorkflowFilterConfig(workflowId);

    workflowFilter.workflowId = workflowId;

    if (!Array.isArray(workflowFilter.filtersList)) {
      workflowFilter.filtersList = [];
    }

    this.dataSetConfig.workflowFilters[index] = workflowFilter;

    return workflowFilter;
  }

  private setWorkflowFilterConfigAtIndex(
    index: number,
    workflowFilter: WorkflowFilter | null | undefined
  ) {
    const workflowId = this.dataSetConfig?.workflowIds?.[index] || '';

    if (!Array.isArray(this.dataSetConfig.workflowFilters)) {
      this.dataSetConfig.workflowFilters = [];
    }

    while (this.dataSetConfig.workflowFilters.length <= index) {
      this.dataSetConfig.workflowFilters.push(this.createWorkflowFilterConfig());
    }

    this.dataSetConfig.workflowFilters[index] = this.cloneWorkflowFilterConfig(
      workflowFilter,
      workflowId
    );
  }

  private buildFiltersFromWorkflowConfig(
    workflowFilter: WorkflowFilter | null | undefined,
    filterParams: FilterBuilderParam[]
  ): FilterBuilderFilter[] {
    if (!workflowFilter?.filtersList?.length || !filterParams?.length) {
      return [];
    }

    return workflowFilter.filtersList
      .map(filterItem => {
        let value = null;
        let valueString = null;

        if (filterItem.searchText && filterItem.searchText.indexOf('{') > -1) {
          value = JSON.parse(filterItem.searchText);
        } else if (filterItem.strOperator == 'in' && filterItem.searchText) {
          value = filterItem.searchText.split(',');
        } else {
          valueString = filterItem.searchText;
        }

        const filterParam = filterParams.find(
          param => param.name === filterItem.title
        );

        if (!filterParam) {
          return null;
        }

        return new FilterBuilderFilter({
          param: filterParam,
          value: valueString,
          queryType: filterItem.strOperator,
          customValue: value,
          inputType: filterItem.inputType,
          dataEntityTypeCode: filterItem.dataEntityTypeCode,
          paramId: filterParam.id
        });
      })
      .filter(filter => filter !== null);
  }

  private buildWorkflowFilterList(
    filtersOutput: any
  ): ItemSearchOptionFieldOption[] {
    const filterKeys = Object.keys(filtersOutput || {});

    return filterKeys.flatMap(key =>
      (filtersOutput[key] || []).map(
        filter =>
          new ItemSearchOptionFieldOption({
            strOperator: filter.type,
            title: filter.id,
            filterText: filter.value,
            searchText: filter.value,
            inputType: filter.inputType,
            dataEntityTypeCode: filter.dataEntityTypeCode
          })
      )
    );
  }

  private createWorkflowFilterConfigFromFilters(
    filters: FilterBuilderFilter[] | null | undefined,
    workflowId: string
  ): WorkflowFilter {
    const workflowFilter = this.createWorkflowFilterConfig(workflowId);

    workflowFilter.filtersList = (filters || []).map(filter => {
      let value = filter.value;

      if (
        filter.queryType === 'range' &&
        (filter.value === 'custom' ||
          filter.param?.inputType === 'text' ||
          filter.param?.inputType === 'number') &&
        filter.customValue &&
        filter.customValue.start &&
        filter.customValue.end
      ) {
        value = JSON.stringify(filter.customValue);
      } else if (
        filter.queryType === 'in' &&
        Array.isArray(filter.customValue)
      ) {
        value = filter.customValue.join(',');
      }

      return new ItemSearchOptionFieldOption({
        strOperator: filter.queryType,
        title: filter.paramId || filter.param?.id,
        filterText: value,
        searchText: value,
        inputType: filter.inputType || filter.param?.inputType,
        dataEntityTypeCode: filter.dataEntityTypeCode
      });
    });

    return workflowFilter;
  }

  private getCachedRowFilters(
    row: WorkflowRow | null | undefined
  ): FilterBuilderFilter[] {
    if (!row?.filterBuilderId || !row?.filterParams?.length) {
      return [];
    }

    try {
      const stringData = localStorage.getItem(
        `filter-builder-${row.filterBuilderId}`
      );

      if (!stringData) {
        return [];
      }

      return (JSON.parse(stringData) || [])
        .map((filter: FilterBuilderFilterFlat) => {
          const param = (row.filterParams || []).find(
            filterParam => filterParam.id === filter.paramId
          );

          if (!param) {
            return null;
          }

          return FilterBuilderFilter.unflatten({
            ...filter,
            param
          });
        })
        .filter(filter => !!filter);
    } catch {
      return [];
    }
  }

  private getCopySourceRow(
    workflowId: string,
    excludedRowKey?: string
  ): WorkflowRow | null {
    return (
      this.rows.find(
        row => row.rowKey !== excludedRowKey && row.workflowId === workflowId
      ) || null
    );
  }

  private hasWorkflowOccurrence(
    workflowId: string | null | undefined,
    excludedIndex?: number
  ): boolean {
    if (!workflowId) {
      return false;
    }

    return (this.dataSetConfig.workflowIds || []).some(
      (id, index) => index !== excludedIndex && id === workflowId
    );
  }

  private refreshViewFromDataSet() {
    if (!this.dataSetConfig) {
      return;
    }

    const fields = this.dataSetConfig.fields || [];
    const workflowIds = this.dataSetConfig.workflowIds || [];
    const previousRowsByRowKey: { [rowKey: string]: WorkflowRow } = {};

    this.syncWorkflowRowKeys();
    this.pruneWorkflowState(workflowIds);

    this.rows.forEach(row => {
      if (row?.rowKey) {
        previousRowsByRowKey[row.rowKey] = row;
      }
    });

    this.columns = fields.map((field: DataSetField) => ({
      id: field.id,
      name: field.name,
      resultHeader: field.resultHeader,
      dataType: field.dataType,
      mappings: field.mappings || [],
      outputFormula: field.outputFormula || null,
      conditionalOutput: field.conditionalOutput || false,
      isStatic: field.isStatic === true
    }));

    this.rows = workflowIds.map((workflowId: string, index: number) => {
      const rowKey = this.workflowRowKeys[index];
      const previousRow = previousRowsByRowKey[rowKey];
      const workflowFilterConfig = this.cloneWorkflowFilterConfig(
        this.getWorkflowFilterConfigAtIndex(index),
        workflowId
      );
      const row: WorkflowRow =
        previousRow || {
          rowKey,
          workflow: '',
          workflowId
        };
      const workflowChanged = row.workflowId !== workflowId;

      row.rowKey = rowKey;
      row.workflowId = workflowId;
      row.workflowFilterConfig = workflowFilterConfig;
      row.filterBuilderId =
        !row.filterBuilderId || workflowChanged
          ? this.createFilterBuilderId(row.rowKey, workflowId)
          : row.filterBuilderId;

      fields.forEach((field: DataSetField) => {
        row[field.name] = (field.mappings || [])
          .filter(mapping => mapping.workflowId === workflowId)
          .map(mapping => mapping.templateCode);
      });

      if (!workflowId) {
        row.filterParams = null;
        row.filters = [];
        return row;
      }

      const cachedFilterParams =
        this.workflowFilterParamsCache[workflowId] || previousRow?.filterParams || null;
      const cachedRowFilters = this.getCachedRowFilters(previousRow);

      row.filterParams = cachedFilterParams;
      row.filters =
        cachedRowFilters.length && !workflowChanged
          ? cachedRowFilters
          : this.buildFiltersFromWorkflowConfig(
              workflowFilterConfig,
              cachedFilterParams || []
            );

      return row;
    });

    this.attachWorkflowNames();
    this.rows.forEach(row => {
      if (row.workflowId && !row.filterParams) {
        this.loadRowFilterData(row);
      }
    });
  }

  private emitDatasetChange() {
    this.refreshViewFromDataSet();
    this.datasetChange.emit({
      fields: this.dataSetConfig.fields,
      workflowIds: this.dataSetConfig.workflowIds,
      workflowFilters: this.dataSetConfig.workflowFilters
    });
  }

  private syncColumnIndexes() {
    (this.dataSetConfig.fields || []).forEach((field: DataSetField, index) => {
      field.mappings = (field.mappings || []).map(mapping => ({
        ...mapping,
        columnIndex: index + 1
      }));
    });
  }

  private getField(columnName: string): DataSetField | undefined {
    return (this.dataSetConfig.fields || []).find(
      (field: DataSetField) => field.name === columnName
    );
  }

  private attachWorkflowNames(): void {
    if (!this.rows.length) {
      return;
    }

    this.rows.forEach(row => {
      row.workflow = this.getWorkflowDisplayName(row.workflowId, '');
    });
  }

  getWorkflowDisplayName(
    workflowId?: string | null,
    fallback = 'Select Workflow'
  ): string {
    if (!workflowId) {
      return fallback;
    }

    if (workflowId === this._context.previousSystemDataWorkflowId) {
      return 'Previous System Data';
    }

    if (workflowId === this.ALL_WORKFLOWS_ID) {
      return 'All Workflows';
    }

    const normalizedWorkflowId = workflowId.toLowerCase();
    const workflow = (this.availableWorkflows || []).find((w: any) => {
      const candidateIds = [
        w?.id,
        w?.versionId,
        w?.workflowId,
        w?.version?.id,
        w?.version?.workflowId
      ]
        .filter(Boolean)
        .map((value: string) => value.toLowerCase());

      return candidateIds.includes(normalizedWorkflowId);
    });

    return workflow?.name || workflow?.version?.name || workflowId || fallback;
  }

  buildDatasetFields() {
    this.ensureDataSetConfigDefaults();
    this.syncColumnIndexes();
    this.emitDatasetChange();
  }

  openColumnSettings() {
    this.openColumnSettingsRequested.emit({
      workflowId: this.selectedWorkflowId
    });
  }

  editColumn(column: Column) {
    const field = this.getField(column?.name);

    if (!field) {
      return;
    }

    this.openColumnSettingsRequested.emit({
      workflowId: this.selectedWorkflowId,
      field: { ...field }
    });
  }

  handleColumnSave(field: any) {
    this.ensureDataSetConfigDefaults();

    const existingColumnIndex = this.dataSetConfig.fields.findIndex(
      (existingField: DataSetField) =>
        existingField.id === field.id || existingField.name === field.name
    );

    if (existingColumnIndex > -1) {
      this.dataSetConfig.fields[existingColumnIndex] = {
        ...this.dataSetConfig.fields[existingColumnIndex],
        ...field,
        mappings: this.dataSetConfig.fields[existingColumnIndex].mappings || []
      };
    } else {
      this.dataSetConfig.fields.push({
        ...field,
        mappings: field.mappings || []
      });
    }

    this.buildDatasetFields();
  }

  deleteColumn(index: number) {
    this.ensureDataSetConfigDefaults();
    this.dataSetConfig.fields.splice(index, 1);
    this.buildDatasetFields();
  }

  deleteColumnByColumn(column: Column) {
    const fields = this.dataSetConfig.fields || [];
    const index = fields.findIndex(
      (field: DataSetField) => field.name === column?.name
    );

    if (index > -1 && fields[index].isStatic !== true) {
      this.deleteColumn(index);
    }
  }

  moveColumnLeft(column: Column) {
    this.moveColumn(column, -1);
  }

  moveColumnRight(column: Column) {
    this.moveColumn(column, 1);
  }

  reorderColumn(event: {
    column: Column;
    targetColumn: Column;
    previousIndex: number;
    currentIndex: number;
  }) {
    if (!event?.column || !event?.targetColumn) {
      return;
    }

    const fields = this.dataSetConfig.fields || [];
    const sourceIndex = fields.findIndex(field => field.name === event.column.name);
    const targetIndex = fields.findIndex(field => field.name === event.targetColumn.name);

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return;
    }

    const [field] = fields.splice(sourceIndex, 1);
    fields.splice(targetIndex, 0, field);
    this.buildDatasetFields();
  }

  addWorkflow() {
    this.ensureDataSetConfigDefaults();
    this.workflowRowKeys.push(this.createRowKey());
    this.dataSetConfig.workflowIds.push('');
    this.dataSetConfig.workflowFilters.push(this.createWorkflowFilterConfig());
    this.buildDatasetFields();
  }

  deleteWorkflow(index: number) {
    const workflowId = this.dataSetConfig.workflowIds[index];

    this.workflowRowKeys.splice(index, 1);
    this.dataSetConfig.workflowIds.splice(index, 1);
    this.dataSetConfig.workflowFilters.splice(index, 1);

    if (!this.hasWorkflowOccurrence(workflowId)) {
      this.clearWorkflowArtifacts(workflowId);
    }

    this.buildDatasetFields();
  }

  selectWorkflow(index: number, workflow: Workflow) {
    const workflowId = workflow.id;
    const currentRow = this.rows[index];
    const copySourceRow = this.getCopySourceRow(workflowId, currentRow?.rowKey);
    const copiedFilterParams =
      copySourceRow?.filterParams || this.workflowFilterParamsCache[workflowId] || null;
    const copiedFilters = this.getCachedRowFilters(copySourceRow);
    const copiedFilterConfig = this.cloneWorkflowFilterConfig(
      copySourceRow?.workflowFilterConfig?.filtersList?.length
        ? copySourceRow.workflowFilterConfig
        : this.createWorkflowFilterConfigFromFilters(
            copiedFilters.length ? copiedFilters : copySourceRow?.filters || [],
            workflowId
          ),
      workflowId
    );
    const previousWorkflowId =
      currentRow?.workflowId || this.dataSetConfig.workflowIds[index];

    if (copiedFilterParams) {
      this.workflowFilterParamsCache[workflowId] = copiedFilterParams;
    }

    this.selectedWorkflowId = workflowId;
    this.dataSetConfig.workflowIds[index] = workflowId;
    this.setWorkflowFilterConfigAtIndex(index, copiedFilterConfig);

    if (
      previousWorkflowId &&
      previousWorkflowId !== workflowId &&
      !this.hasWorkflowOccurrence(previousWorkflowId, index)
    ) {
      this.clearWorkflowArtifacts(previousWorkflowId);
    }

    this.buildDatasetFields();
  }

  openCellDropdown(row: WorkflowRow, col: Column) {
    if (!row.workflowId) {
      this.cellDropdownEntities = [];
      return;
    }

    this._workflowSvc.getWorkflowEntities(row.workflowId).subscribe(entities => {
      this.cellDropdownEntities = entities || [];
    });
  }

  private clearWorkflowArtifacts(workflowId?: string | null) {
    if (!workflowId) {
      return;
    }

    delete this.workflowFilterParamsCache[workflowId];

    (this.dataSetConfig.fields || []).forEach((field: DataSetField) => {
      field.mappings = (field.mappings || []).filter(
        mapping => mapping.workflowId !== workflowId
      );
    });
  }

  private pruneWorkflowState(workflowIds: string[]) {
    const validWorkflowIds = new Set((workflowIds || []).filter(Boolean));

    Object.keys(this.workflowFilterParamsCache).forEach(workflowId => {
      if (!validWorkflowIds.has(workflowId)) {
        delete this.workflowFilterParamsCache[workflowId];
      }
    });
  }

  private createFilterBuilderId(
    rowKey: string,
    workflowId?: string | null
  ): string {
    const workflowKey = workflowId || 'workflow';
    return `dataSetFilters_${workflowKey}_${rowKey}`;
  }

  selectCellEntity(row: WorkflowRow, col: Column, entity: any) {
    const templateCode = entity?.templateCode || entity?.name;
    const field = this.getField(col.name);

    if (!templateCode || !row.workflowId || !field) {
      return;
    }

    if (!Array.isArray(field.mappings)) {
      field.mappings = [];
    }

    field.mappings.push({
      workflowId: row.workflowId,
      columnIndex:
        this.dataSetConfig.fields.findIndex(
          (existingField: DataSetField) => existingField.name === field.name
        ) + 1,
      templateCode
    });

    this.buildDatasetFields();
  }

  deleteCellValue(row: WorkflowRow, col: Column, index: number) {
    const field = this.getField(col.name);

    if (!field || !row.workflowId) {
      return;
    }

    const mappingsForWorkflow = (field.mappings || []).filter(
      mapping => mapping.workflowId === row.workflowId
    );
    const mappingToRemove = mappingsForWorkflow[index];

    if (!mappingToRemove) {
      return;
    }

    field.mappings = (field.mappings || []).filter(
      mapping => mapping !== mappingToRemove
    );

    this.buildDatasetFields();
  }

  getInputType(entity: any): string {
    if (entity.dataEntityTypeCode === 'date') {
      return 'date';
    }

    if (entity.dataEntityTypeCode === 'numeric-data') {
      return 'number';
    }

    if (entity.dataEntityTypeCode === 'list-data') {
      return 'select';
    }

    return 'text';
  }

  private moveColumn(column: Column, direction: -1 | 1) {
    const fields = this.dataSetConfig.fields || [];
    const currentIndex = fields.findIndex(field => field.name === column?.name);
    const targetIndex = currentIndex + direction;

    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= fields.length
    ) {
      return;
    }

    const [field] = fields.splice(currentIndex, 1);
    fields.splice(targetIndex, 0, field);
    this.buildDatasetFields();
  }

  private loadRowFilterData(row: WorkflowRow) {
    if (!row?.workflowId) {
      row.filterParams = null;
      row.filters = [];
      return;
    }

    const rowIndex = this.rows.findIndex(r => r.rowKey === row.rowKey);
    const workflowFilterConfig =
      rowIndex > -1
        ? this.getWorkflowFilterConfigAtIndex(rowIndex)
        : this.createWorkflowFilterConfig(row.workflowId);
    const cachedFilterParams = this.workflowFilterParamsCache[row.workflowId];

    if (cachedFilterParams) {
      row.filterParams = cachedFilterParams;
      row.workflowFilterConfig = this.cloneWorkflowFilterConfig(
        workflowFilterConfig,
        row.workflowId
      );
      row.filters = this.buildFiltersFromWorkflowConfig(
        workflowFilterConfig,
        cachedFilterParams
      );
      return;
    }

    this.filterWorkflow(row.workflowId, workflowFilterConfig).then(result => {
      const currentRow = this.rows.find(r => r.rowKey === row.rowKey);

      if (!currentRow || currentRow.workflowId !== row.workflowId) {
        return;
      }

      this.workflowFilterParamsCache[row.workflowId] = result.filterParams;
      currentRow.workflowFilterConfig = this.cloneWorkflowFilterConfig(
        workflowFilterConfig,
        currentRow.workflowId
      );
      currentRow.filterParams = result.filterParams;
      currentRow.filters = result.filters;
    });
  }

  onRowFiltersChange2(e, row: WorkflowRow) {
    if (!row?.workflowId) {
      return;
    }

    const rowIndex = this.rows.findIndex(r => r.rowKey === row.rowKey);

    if (rowIndex < 0) {
      return;
    }

    const workflowFilterConfig = this.getWorkflowFilterConfigAtIndex(rowIndex);

    workflowFilterConfig.workflowId = row.workflowId;
    workflowFilterConfig.filtersList = this.buildWorkflowFilterList(e);
    row.workflowFilterConfig = this.cloneWorkflowFilterConfig(
      workflowFilterConfig,
      row.workflowId
    );
    row.filters = this.buildFiltersFromWorkflowConfig(
      workflowFilterConfig,
      row.filterParams || []
    );
    this.setWorkflowFilterConfigAtIndex(rowIndex, workflowFilterConfig);
  }

  trackRowByWorkflow = (_index: number, row: WorkflowRow) =>
    row?.rowKey || `pending-${_index}`;

  trackColumnByName = (_index: number, col: Column) => col?.name || _index;

  async filterWorkflow(
    workflowId: string,
    workflowFilterConfig?: WorkflowFilter | null
  ): Promise<{
    filterParams: FilterBuilderParam[];
    filters: FilterBuilderFilter[];
  }> {
    this.workflowFilterParams = null;
    this.workflowFilters = null;
    this.workflowFilterConfig = null;

    const filter = this.cloneWorkflowFilterConfig(
      workflowFilterConfig,
      workflowId
    );

    let entityObs = null;

    if (workflowId != this._context.previousSystemDataWorkflowId) {
      if (workflowId !== this.ALL_WORKFLOWS_ID) {
        entityObs = this._workflowSvc.getDraftWorkflowEntities(workflowId);
      } else {
        entityObs = this._workflowSvc.getDataEntities(null, null, true);
      }
    } else {
      const fields = await this._workflowSvc.getPreviousSystemDataFields(
        this._context.client.id
      );

      entityObs = of(
        fields.map(e => {
          if (e.dataType == ReportDataSetFieldDataType.Date) {
            return new DateDataEntity({
              templateCode: e.name,
              label: e.name
            });
          }

          return new FreeFormTextDataEntity({
            templateCode: e.name,
            label: e.name
          });
        })
      );
    }

    return await new Promise(resolve => {
    entityObs.subscribe(entities => {
      const filterParams: FilterBuilderParam[] = entities.map(e => {
        return {
          name: e.templateCode,
          id: e.templateCode,
          paramId: e.templateCode,
          inputType: DataSet.resolveInputType(e),
          dataEntityTypeCode: e.Type,
          types: DataSet.resolveTypes(e),
          options: (() => {
            const os = DataSet.resolveOptions(e);

            if (os) {
              return os.map(o => {
                return { name: o, value: o };
              });
            }

            return null;
          })()
        };
      });

      const filters = this.buildFiltersFromWorkflowConfig(filter, filterParams);

      this.workflowFilterConfig = filter;
      this.workflowFilters = filters.filter(f => f);
      this.workflowFilterParams = filterParams;
      this.showFilterBuilder = true;
      resolve({
        filterParams,
        filters: filters.filter(f => f)
      });
    });
    });
  }
}
